import { supabase } from "@/lib/supabase";
import {
  getCompletionEstimate,
  getDailyStreak,
  getNewWordsTrend,
  getProgressBarValues,
  getTotalAnsweredCards,
} from "@/Scripts/dbFunctions";

const PAGE_SIZE = 1000;
const DETAIL_DAYS = 30;
const ALLOWED_DETAIL_DAYS = new Set([7, 30, 180]);

function rangeLabel(days) {
  return days === 180 ? "6 months" : `${days} days`;
}

function dayKey(value) {
  const date = value instanceof Date ? value : new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function dayLabel(key) {
  const [year, month, day] = key.split("-").map(Number);
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
  }).format(new Date(year, month - 1, day));
}

function windowStart(days = DETAIL_DAYS) {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - (days - 1));
  return start;
}

function emptyDailySeries(days = DETAIL_DAYS) {
  const start = windowStart(days);
  return Array.from({ length: days }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    const key = dayKey(date);
    return { date: key, label: dayLabel(key), value: 0 };
  });
}

async function fetchAll(makeQuery) {
  const rows = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await makeQuery(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    rows.push(...(data || []));
    if (!data || data.length < PAGE_SIZE) break;
  }
  return rows;
}

async function getPreferences(userId) {
  const { data, error } = await supabase
    .from("user_preferences")
    .select("active_language, daily_card_goal")
    .eq("user_id", userId)
    .maybeSingle();
  if (error) throw error;
  return {
    language: data?.active_language || null,
    dailyGoal: Number(data?.daily_card_goal) || 0,
  };
}

async function getActions(userId, language, days, columns) {
  const startISO = windowStart(days).toISOString();
  return fetchAll((from, to) => {
    let query = supabase
      .from("card_actions")
      .select(columns)
      .eq("user_id", userId)
      .gte("date", startISO)
      .order("date", { ascending: true })
      .range(from, to);
    if (language) query = query.eq("language", language);
    return query;
  });
}

function bucketActions(rows, days, valueForRows) {
  const buckets = new Map();
  for (const row of rows) {
    const key = dayKey(row.date);
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(row);
  }
  return emptyDailySeries(days).map((day) => ({
    ...day,
    value: valueForRows(buckets.get(day.date) || []),
  }));
}

function sumLast(series, days) {
  return series.slice(-days).reduce((sum, item) => sum + item.value, 0);
}

function rounded(value, places = 1) {
  const factor = 10 ** places;
  return Math.round(value * factor) / factor;
}

async function progressDetail(userId) {
  const values = await getProgressBarValues(userId);
  const breakdown = [
    { label: "Seen, still new", value: values?.total_zeros || 0, color: "#DCDCDC" },
    { label: "Learning", value: values?.total_ones || 0, color: "#1E93AB" },
    { label: "Review", value: values?.total_twos || 0, color: "#44D281" },
    { label: "Relearning", value: values?.total_threes || 0, color: "#D28A44" },
  ];
  const learned = breakdown.reduce((sum, item) => sum + item.value, 0);
  return {
    headline: Math.floor((Math.min(5000, learned) / 5000) * 100),
    unit: "% of 5,000",
    description: "Words you have encountered at least once in your active language.",
    formula:
      "All active-language cards with at least one review, including cards currently in learning, review, and relearning states.",
    breakdown,
    summary: [
      { label: "Words encountered", value: learned.toLocaleString() },
      { label: "Words remaining", value: Math.max(0, 5000 - learned).toLocaleString() },
    ],
  };
}

async function workloadDetail(userId, language, days) {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + (days - 1));
  end.setHours(23, 59, 59, 999);

  const [upcoming, overdueResult] = await Promise.all([
    language
      ? fetchAll((from, to) =>
          supabase
            .from("user_cards")
            .select("due")
            .eq("user_id", userId)
            .eq("language", language)
            .gte("due", start.toISOString())
            .lte("due", end.toISOString())
            .order("due", { ascending: true })
            .range(from, to)
        )
      : Promise.resolve([]),
    language
      ? supabase
          .from("user_cards")
          .select("id", { count: "exact", head: true })
          .eq("user_id", userId)
          .eq("language", language)
          .lt("due", start.toISOString())
          .gte("reps", 1)
      : Promise.resolve({ count: 0, error: null }),
  ]);
  if (overdueResult.error) throw overdueResult.error;

  const counts = new Map();
  for (const row of upcoming) {
    if (!row?.due) continue;
    const key = dayKey(row.due);
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  const series = Array.from({ length: days }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    const key = dayKey(date);
    return { date: key, label: dayLabel(key), value: counts.get(key) || 0 };
  });
  const period = rangeLabel(days);
  return {
    headline: overdueResult.count || 0,
    unit: "overdue",
    description: `Previously reviewed cards that are due now, plus ${period} of scheduled reviews.`,
    formula:
      "Overdue cards have a due date before today and at least one prior review. The chart groups future due dates by your local calendar day.",
    series,
    summary: [
      { label: "Due today", value: series[0]?.value || 0 },
      {
        label: `Next ${Math.min(7, days)} days`,
        value: series.slice(0, Math.min(7, days)).reduce((sum, day) => sum + day.value, 0),
      },
      { label: `Next ${period}`, value: sumLast(series, days) },
    ],
  };
}

async function goalDetail(userId, dailyGoal, days) {
  const rows = await getActions(userId, null, days, "date");
  const series = bucketActions(rows, days, (actions) => actions.length);
  const today = series[series.length - 1]?.value || 0;
  const period = rangeLabel(days);
  return {
    headline: Math.max(0, dailyGoal - today),
    unit: "cards left today",
    description: "Your completed review sessions across all languages compared with your daily target.",
    formula:
      `Daily goal minus the number of completed card actions since local midnight. The chart shows the selected ${period}.`,
    series,
    goalLine: dailyGoal,
    summary: [
      { label: "Daily goal", value: dailyGoal },
      { label: "Completed today", value: today },
      { label: `${period} total`, value: sumLast(series, days) },
    ],
  };
}

async function streakDetail(userId, days) {
  const [streak, rows] = await Promise.all([
    getDailyStreak(userId),
    getActions(userId, null, days, "date"),
  ]);
  const series = bucketActions(rows, days, (actions) => actions.length);
  const activeDays = series.filter((day) => day.value > 0).length;
  const reviews = sumLast(series, days);
  const period = rangeLabel(days);
  return {
    headline: streak,
    unit: streak === 1 ? "day" : "days",
    description: "Consecutive calendar days with at least one completed review. Yesterday keeps the streak alive until today ends.",
    formula:
      "Starting with today when active, otherwise yesterday, count backward until the first day with no completed card action.",
    series,
    seriesMode: "activity",
    summary: [
      { label: `Active days in ${period}`, value: activeDays },
      { label: `Reviews in ${period}`, value: reviews },
      {
        label: "Avg. reviews per active day",
        value: activeDays ? rounded(reviews / activeDays, 1) : 0,
      },
    ],
  };
}

async function accuracyDetail(userId, language, days) {
  const rows = await getActions(userId, language, days, "date, correct");
  const series = bucketActions(rows, days, (actions) => {
    if (!actions.length) return 0;
    return Math.round(
      (actions.filter((action) => action.correct === true).length / actions.length) * 100
    );
  });
  const correct = rows.filter((row) => row.correct === true).length;
  const percent = rows.length ? Math.round((correct / rows.length) * 100) : 0;
  const period = rangeLabel(days);
  return {
    headline: percent,
    unit: "%",
    description: "The share of cards completed correctly on the first try in your active language.",
    formula:
      `First-try completions divided by all completed card sessions during the selected ${period}.`,
    series,
    maxValue: 100,
    summary: [
      { label: "First-try correct", value: correct },
      { label: "Cards completed", value: rows.length },
      { label: "Needed a retry", value: rows.length - correct },
    ],
  };
}

async function timeDetail(userId, language, days) {
  const rows = await getActions(
    userId,
    language,
    days,
    "date, duration_ms"
  );
  const valid = (items) =>
    items
      .map((item) => Number(item.duration_ms))
      .filter((value) => Number.isFinite(value) && value >= 0);
  const series = bucketActions(rows, days, (actions) => {
    const values = valid(actions);
    return values.length
      ? rounded(values.reduce((sum, value) => sum + value, 0) / values.length / 1000)
      : 0;
  });
  const allValues = valid(rows);
  const average = allValues.length
    ? rounded(
        allValues.reduce((sum, value) => sum + value, 0) /
          allValues.length /
          1000
      )
    : 0;
  const activeDays = series.filter((day) => day.value > 0).length;
  const period = rangeLabel(days);
  return {
    headline: average,
    unit: "seconds",
    description: "Average elapsed time from a card appearing until it is completed, including retries.",
    formula:
      `Sum of recorded completion durations divided by cards with timing data during the selected ${period}.`,
    series,
    summary: [
      { label: "Timed cards", value: allValues.length },
      { label: `${period} average`, value: `${average}s` },
      { label: "Days with timing data", value: activeDays },
    ],
  };
}

async function reviewsPerCardDetail(userId, language, days) {
  const rows = await getActions(userId, language, days, "date, card_id");
  const series = bucketActions(rows, days, (actions) => {
    if (!actions.length) return 0;
    return rounded(actions.length / new Set(actions.map((row) => row.card_id)).size, 2);
  });
  const unique = new Set(rows.map((row) => row.card_id)).size;
  const average = unique ? rounded(rows.length / unique, 2) : 0;
  const period = rangeLabel(days);
  return {
    headline: average,
    unit: "reviews per card",
    description: "How often the same cards reappeared among completed review sessions.",
    formula:
      `Completed card sessions divided by distinct card IDs during the selected ${period}.`,
    series,
    summary: [
      { label: "Review sessions", value: rows.length },
      { label: "Distinct cards", value: unique },
      { label: `${period} average`, value: average },
    ],
  };
}

async function newWordsDetail(userId, days) {
  const trend = await getNewWordsTrend(userId, days);
  const series = trend.map((day) => ({
    date: day.date,
    label: dayLabel(day.date),
    value: day.count,
  }));
  const total = sumLast(series, days);
  const activeDays = series.filter((day) => day.value > 0).length;
  const period = rangeLabel(days);
  return {
    headline: total,
    unit: "new words",
    description: "Words whose first-ever completed review happened during the selected period.",
    formula:
      "A word counts only when its card has no completed action before that calendar day. Repeat reviews never count as new.",
    series,
    summary: [
      { label: `New words in ${period}`, value: total },
      { label: "Active learning days", value: activeDays },
      { label: "Average per day", value: rounded(total / days, 1) },
    ],
  };
}

async function totalAnsweredDetail(userId, days) {
  const [total, rows] = await Promise.all([
    getTotalAnsweredCards(userId),
    getActions(userId, null, days, "date"),
  ]);
  const series = bucketActions(rows, days, (actions) => actions.length);
  const periodTotal = sumLast(series, days);
  const period = rangeLabel(days);
  return {
    headline: Number(total).toLocaleString(),
    unit: "total reviews",
    description: "All completed card-review sessions across every language.",
    formula:
      "Exact all-time count of card action records. Repeated reviews of the same card count separately.",
    series,
    summary: [
      { label: period, value: periodTotal },
      { label: "Daily average", value: rounded(periodTotal / days, 1) },
      { label: "All time", value: Number(total).toLocaleString() },
    ],
  };
}

async function completionDetail(userId, days) {
  const [completion, trend] = await Promise.all([
    getCompletionEstimate(userId, days),
    getNewWordsTrend(userId, days),
  ]);
  const series = trend.map((day) => ({
    date: day.date,
    label: dayLabel(day.date),
    value: day.count,
  }));
  const eta = completion?.eta
    ? new Intl.DateTimeFormat(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(new Date(completion.eta))
    : "Not available";
  const period = rangeLabel(days);
  return {
    headline: completion?.days == null ? "—" : completion.days,
    unit: completion?.days == null ? "No recent pace" : "days remaining",
    description: "A pace-based estimate for completing the remaining unseen words in your active language.",
    formula:
      `Remaining cards with zero reviews divided by genuine first-ever words per day over the selected ${period}. No estimate is shown when that pace is zero.`,
    series,
    summary: [
      { label: "Unseen words", value: completion?.remaining_new || 0 },
      { label: `${period} pace`, value: `${completion?.avg_new_per_day || 0}/day` },
      { label: "Estimated finish", value: eta },
    ],
  };
}

export const STAT_DETAIL_META = Object.freeze({
  progress: { title: "My Progress", hasRange: false },
  workload: { title: "Upcoming Workload", hasRange: true },
  "today-goal": { title: "Today's Goal", hasRange: true },
  streak: { title: "Daily Streak", hasRange: true },
  accuracy: { title: "First-Try Accuracy", hasRange: true },
  "average-time": { title: "Avg. Time per Card", hasRange: true },
  "reviews-per-card": { title: "Avg Reviews per Card", hasRange: true },
  "new-words": { title: "New Words", hasRange: true },
  "total-answered": { title: "Total Answered", hasRange: true },
  completion: { title: "Completion Estimate", hasRange: true },
});

export async function getStatDetailData(userId, statKey, days = DETAIL_DAYS) {
  if (!userId || !STAT_DETAIL_META[statKey]) return null;
  const selectedDays = ALLOWED_DETAIL_DAYS.has(Number(days))
    ? Number(days)
    : DETAIL_DAYS;
  const preferences = await getPreferences(userId);
  const handlers = {
    progress: () => progressDetail(userId),
    workload: () => workloadDetail(userId, preferences.language, selectedDays),
    "today-goal": () => goalDetail(userId, preferences.dailyGoal, selectedDays),
    streak: () => streakDetail(userId, selectedDays),
    accuracy: () => accuracyDetail(userId, preferences.language, selectedDays),
    "average-time": () => timeDetail(userId, preferences.language, selectedDays),
    "reviews-per-card": () => reviewsPerCardDetail(userId, preferences.language, selectedDays),
    "new-words": () => newWordsDetail(userId, selectedDays),
    "total-answered": () => totalAnsweredDetail(userId, selectedDays),
    completion: () => completionDetail(userId, selectedDays),
  };
  return handlers[statKey]();
}
